<template>
  <div>
    <div class="field">
      <div class="control">
        <div class="select is-fullwidth">
          <select v-model="selectedOperationId" @change="operationSelected">
            <option v-for="operation in operations" :key="operation.id" :value="operation.id">
              {{ operation.title }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <div class="field is-grouped" v-if="requiresInput">
      <div class="control">
        <input
          :value="liveCondition.value"
          type="text"
          class="input"
          @input="(e) => textChanged((e.target as HTMLInputElement).value)"
        />
        <p class="help is-danger" v-if="isRequiredInvalid">Number of {{ selectedUnitValue }} required</p>
        <p class="help is-danger" v-else-if="isNumericInvalid">Must be numeric</p>
        <p class="help is-danger" v-else-if="isMaxLengthInvalid">Must be a maximum of 8 characters</p>
      </div>
      <div class="relative-text">
        <span>{{ selectedUnitValue }} ago</span>
      </div>
    </div>

    <div class="control">
      <n-button variant="primary" @click="complete()">Done</n-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Conditions, OperationPickerOption, ConditionOperationType } from './../models/conditions';
import { ConditionViewModel } from './../models/conditionViewModel';
import { required, numeric, maxLength, requiredIf } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { PropType, ref, onMounted, computed } from 'vue';
import VuelidateUtil from '@shared/util/vuelidateUtil';

const props = defineProps({
  condition: {
    type: Object as PropType<ConditionViewModel>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'changed', condition: ConditionViewModel): void;
  (e: 'completed', condition: ConditionViewModel): void;
}>();

const liveCondition = ref<ConditionViewModel>({
  attribute: '',
  operation: '',
  type: '',
  value: '',
  result: true,
});

const validations = computed(() => ({
  liveCondition: {
    value: {
      required: requiredIf(() => liveCondition.value.operation !== ConditionOperationType.IsSet),
      numeric,
      maxLength: maxLength(8),
    },
  },
}));

const $v = useVuelidate(validations, { liveCondition });
const isRequiredInvalid = computed(() => VuelidateUtil.isRequiredInvalid($v, (v) => v.liveCondition.value));
const isNumericInvalid = computed(() => VuelidateUtil.isValidationInvalid($v, (v) => v.liveCondition.value, 'numeric'));
const isMaxLengthInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, (v) => v.liveCondition.value, 'maxLength'),
);

const operations: OperationPickerOption[] = Conditions.Operations.datetime.map((op, index) => {
  return {
    id: `${index}`,
    title: op.title,
    operation: op.operation,
    result: op.result,
  };
});

const units = Conditions.DateTimeUnits;

const selectedOperationId = ref('');
const selectedUnitValue = ref<string | undefined>('');
const requiresInput = computed(() => liveCondition.value.operation !== ConditionOperationType.IsSet);

onMounted(() => {
  if (props.condition) {
    liveCondition.value = {
      attribute: props.condition.attribute,
      operation: props.condition.operation,
      type: props.condition.type,
      value: props.condition.value,
      result: props.condition.result,
      unit: props.condition.unit,
      invalid: props.condition.invalid,
    };

    if (liveCondition.value.invalid) {
      $v.value.$touch();
    }
  }

  selectedOperationId.value = getSelectedOperation();
  operationSelected();

  selectedUnitValue.value = getSelectedDateTimeUnit();
  unitSelected();
});

function getSelectedOperation() {
  if (liveCondition.value) {
    const selectedOperation = operations.find(
      (op) => op.operation === liveCondition.value.operation && op.result === liveCondition.value.result,
    );
    if (selectedOperation) {
      return selectedOperation.id;
    }
  }

  return operations[0].id;
}

function getSelectedDateTimeUnit() {
  if (liveCondition.value && liveCondition.value.unit) {
    return liveCondition.value.unit as string;
  } else if (liveCondition.value.operation !== ConditionOperationType.IsSet) {
    return units[0].id;
  }
}

function getSelectedValue() {
  if (liveCondition.value) {
    return liveCondition.value.value;
  }
  return '1';
}

function operationSelected() {
  const selectedOperation = operations.find((op) => op.id === selectedOperationId.value);
  if (selectedOperation) {
    liveCondition.value.operation = selectedOperation.operation;
    liveCondition.value.result = selectedOperation.result;
    emitConditionChanged();
    selectedUnitValue.value = getSelectedDateTimeUnit();
    unitSelected();
  }
}

function unitSelected() {
  const selectedUnit = units.find((op) => op.id === selectedUnitValue.value);
  if (selectedUnit) {
    liveCondition.value.unit = selectedUnit.id;
  } else {
    liveCondition.value.unit = undefined;
  }
  emitConditionChanged();
}

function textChanged(value: string) {
  liveCondition.value.value = value;
  $v.value.$touch();
  emitConditionChanged();
}

function emitConditionChanged() {
  if (props.condition.isNew) {
    props.condition.isNew = false;
  } else {
    liveCondition.value.invalid = $v.value.$invalid && requiresInput.value;
    emit('changed', liveCondition.value);
  }
}

function complete() {
  liveCondition.value.invalid = $v.value.$invalid && requiresInput.value;
  emit('completed', liveCondition.value);
}
</script>

<style lang="scss" scoped>
.field.is-grouped > .control {
  flex-shrink: 1;
}

.relative-text {
  margin-right: 0.75rem;
  display: flex;
  align-items: center;
  height: 2.25em;
}
</style>
